import { getAddress } from 'viem';

import { EventArgs, EvmContractName, EvmNFTChain } from '@repo/types-client';

import { contractMapping } from './contract-mapping';

/**
 * Retrieves the ABI inputs for a specific event in a smart contract
 * @param contractName - The capitalized name of the EVM contract
 * @param eventName - The name of the event to get inputs for
 * @param chain - The blockchain network identifier
 * @returns The ABI inputs for the specified event, or undefined if not found
 */
const getContractABIInputs = (
  contractName: Capitalize<EvmContractName>,
  eventName: string,
  chain: EvmNFTChain,
) => {
  // Get contract data for the specified chain and contract
  const contractData = contractMapping[chain as keyof typeof contractMapping][
    contractName as keyof (typeof contractMapping)[keyof typeof contractMapping]
  ] as {
    address: string;
    abi: Array<{
      type: string;
      name: string;
      inputs?: Array<{ name: string; type: string }>;
    }>;
  };

  // Filter ABI to find matching event
  const abiInputsData = contractData.abi.filter(
    (abiItem) => abiItem.type === 'event' && abiItem.name === eventName,
  );

  if (abiInputsData.length) {
    return abiInputsData[0]?.inputs;
  }
};

/**
 * Parses event parameters into typed arguments based on the contract's ABI
 * @param contractName - The capitalized name of the EVM contract
 * @param eventSignature - The event signature/name to parse
 * @param eventParams - Array of raw event parameters
 * @param chain - The blockchain network identifier
 * @returns Object containing parsed and typed event arguments
 */
export const parseArgsFromEventParams = (
  contractName: Capitalize<EvmContractName>,
  eventSignature: string,
  eventParams: string[],
  chain: EvmNFTChain,
): EventArgs => {
  const args = {} as EventArgs;
  const contractABIInputs = getContractABIInputs(
    contractName,
    eventSignature,
    chain,
  );

  if (!contractABIInputs) {
    throw new Error(
      `No ABI inputs found for event ${eventSignature} in contract ${contractName}`,
    );
  }

  // Process each event parameter according to its ABI type
  for (const itemIndex in eventParams) {
    const input = contractABIInputs[itemIndex];
    if (!input) continue;

    const param = eventParams[itemIndex];

    // Convert parameter based on its type:
    // - Addresses are converted to checksummed format
    // - Numbers are converted to BigInt
    // - Arrays of numbers are converted to arrays of BigInt
    // - Other types remain unchanged
    const paramValue =
      input.type === 'address' && param
        ? getAddress(param) // Convert address to checksummed address
        : typeof param === 'number'
          ? BigInt(param)
          : Array.isArray(param) && param.every((v) => typeof v === 'number')
            ? param.map((v) => BigInt(v))
            : param;

    if (paramValue !== undefined) {
      args[input.name] = paramValue;
    }
  }
  return args;
};
